import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";
import { useLocale } from "@/lib/i18n/locale";

interface ExistingPatientFormProps {
  /** Source label for CRM tracking. */
  source?: string;
  /** Where to redirect after submit. */
  redirectTo?: string;
}

const INTEREST_OPTIONS_EN = [
  { value: "start-tms", label: "Start TMS" },
  { value: "saint", label: "SAINT®" },
  {
    value: "follow-up",
    label: "Follow-up with psychiatrist or nurse practitioner",
  },
  { value: "therapy", label: "Therapy" },
  { value: "not-sure", label: "Not sure — help me decide" },
];

const INTEREST_OPTIONS_ES = [
  { value: "start-tms", label: "Comenzar TMS" },
  { value: "saint", label: "SAINT®" },
  {
    value: "follow-up",
    label: "Seguimiento con psiquiatra o enfermera",
  },
  { value: "therapy", label: "Terapia" },
  { value: "not-sure", label: "No estoy seguro — ayúdenme a decidir" },
];

/**
 * "Benefits Check & Appointment Request" — existing/returning-patient form.
 * On submit, awaits the CRM submission, then redirects to
 * /thank-you-existing-patient so the Google Ads conversion fires there.
 *
 * Collects only scheduling/benefits data — NO diagnosis, symptoms, or
 * medication lists (those belong in the visit, not a web form).
 *
 * Locale-aware: renders Spanish labels when the route is under /es/*.
 */
export const ExistingPatientForm: React.FC<ExistingPatientFormProps> = ({
  source = "existing-patients",
  redirectTo = "/thank-you-existing-patient",
}) => {
  const { isEs } = useLocale();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [lastVisit, setLastVisit] = useState("");
  const [insuranceChanged, setInsuranceChanged] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [contactMethod, setContactMethod] = useState("");
  const [consent, setConsent] = useState(false);

  const esRedirect = isEs ? "/es/thank-you-existing-patient" : redirectTo;
  const esSource = isEs ? `es-${source}` : source;

  const interestOptions = isEs ? INTEREST_OPTIONS_ES : INTEREST_OPTIONS_EN;

  const t = {
    heading: isEs
      ? "Verificación de beneficios y solicitud de cita"
      : "Benefits Check & Appointment Request",
    subheading: isEs
      ? "¿Ya es paciente? Complete este formulario y nuestros coordinadores verificarán sus beneficios y le contactarán para programar su cita — generalmente dentro de un día hábil."
      : "Already a patient? Fill this out and our care coordinators will verify your benefits and reach out to schedule — usually within one business day.",
    firstName: isEs ? "Nombre *" : "First name *",
    firstNamePh: isEs ? "María" : "Jane",
    lastName: isEs ? "Apellido *" : "Last name *",
    lastNamePh: isEs ? "López" : "Doe",
    phone: isEs ? "Teléfono *" : "Phone *",
    phonePh: isEs ? "(954) 000-0000" : "(954) 000-0000",
    email: isEs ? "Correo electrónico *" : "Email *",
    emailPh: isEs ? "maria@ejemplo.com" : "jane@example.com",
    dob: isEs ? "Fecha de nacimiento *" : "Date of birth *",
    lastVisit: isEs ? "Última visita *" : "Last visit *",
    selectOpt: isEs ? "Seleccione una opción" : "Select an option",
    lastVisitOpts: isEs
      ? [
          { v: "within-3-months", l: "Dentro de 3 meses" },
          { v: "3-12-months", l: "3–12 meses" },
          { v: "over-a-year", l: "Hace más de un año" },
          { v: "not-sure", l: "No estoy seguro" },
        ]
      : [
          { v: "within-3-months", l: "Within 3 months" },
          { v: "3-12-months", l: "3–12 months" },
          { v: "over-a-year", l: "Over a year" },
          { v: "not-sure", l: "Not sure" },
        ],
    insChanged: isEs ? "¿Cambió su seguro? *" : "Insurance changed? *",
    insChangedOpts: isEs
      ? [
          { v: "yes", l: "Sí" },
          { v: "no", l: "No" },
          { v: "not-sure", l: "No estoy seguro" },
        ]
      : [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "not-sure", l: "Not sure" },
        ],
    carrier: isEs ? "Aseguradora (opcional)" : "Carrier (optional)",
    carrierPh: isEs ? "Ej. Blue Cross" : "e.g. Blue Cross",
    interested: isEs
      ? "¿En qué está interesado? *"
      : "What are you interested in? *",
    contactMethod: isEs
      ? "Método de contacto preferido *"
      : "Preferred contact method *",
    contactOpts: isEs
      ? [
          { v: "call", l: "Llamada" },
          { v: "text", l: "Mensaje de texto" },
          { v: "email", l: "Correo electrónico" },
        ]
      : [
          { v: "call", l: "Call" },
          { v: "text", l: "Text" },
          { v: "email", l: "Email" },
        ],
    consent: isEs
      ? "Soy un paciente existente y autorizo a Neuro Wellness a verificar mis beneficios del seguro y contactarme sobre la programación de citas."
      : "I am an existing patient and authorize Neuro Wellness to verify my insurance benefits and contact me about scheduling.",
    submit: isEs
      ? "Solicitar verificación de beneficios y cita"
      : "Request My Benefits Check & Appointment",
    secureNote: isEs
      ? "Este formulario se transmite de forma segura. Por favor no incluya detalles clínicos; los discutiremos en su visita."
      : "This form is transmitted securely. Please do not include clinical details; we'll discuss those at your visit.",
  };

  const toggleInterest = (value: string) => {
    setInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) return;

    const payload = buildTrackingPayload(
      esSource,
      `Existing-patient Benefits Check — ${esSource}`,
    );
    payload.formData = {
      first_name: firstName,
      last_name: lastName,
      phone,
      email,
      date_of_birth: dob,
    };
    payload.formLabels = {
      first_name: isEs ? "Nombre" : "First name",
      last_name: isEs ? "Apellido" : "Last name",
      phone: isEs ? "Teléfono" : "Phone",
      email: isEs ? "Correo electrónico" : "Email",
      date_of_birth: isEs
        ? "Fecha de nacimiento (búsqueda en expediente)"
        : "Date of birth (chart lookup)",
    };

    trackLead(esSource, `Existing-patient Benefits Check — ${esSource}`);
    trackLeadConversion(
      esSource,
      `Existing-patient Benefits Check — ${esSource}`,
    );

    try {
      await postTrackingEvent(payload, {
        customFields: {
          "7hL08XEvTyz3dZG0DCwD": {
            value: lastVisit,
            label: "Approximate date of last visit",
          },
          kQG253kbGs8L8N6X6X8h: {
            value: insuranceChanged,
            label: "Has your insurance changed since your last visit?",
          },
          JVicqcGq2tt4g99nZmxR: {
            value: interests.join(", "),
            label: "What are you interested in?",
          },
          "61OSWxPc6CU0czOc4ejk": {
            value: contactMethod,
            label: "Preferred contact method",
          },
          ...(carrierName
            ? {
                qwYcMnzrsdvJCUTHCuwv: {
                  value: carrierName,
                  label: "Insurance carrier (optional)",
                },
              }
            : {}),
        },
      });
    } catch {
      /* fire-and-forget — still redirect */
    }
    navigate(esRedirect);
  };

  return (
    <div className="w-full min-w-0 rounded-2xl border border-border bg-card p-4 text-foreground shadow-soft sm:p-5">
      <div className="mb-3">
        <h2 className="font-headings text-base font-bold text-primary sm:text-lg">
          {t.heading}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{t.subheading}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ep-first" className="text-xs sm:text-sm">
              {t.firstName}
            </Label>
            <Input
              id="ep-first"
              required
              placeholder={t.firstNamePh}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-last" className="text-xs sm:text-sm">
              {t.lastName}
            </Label>
            <Input
              id="ep-last"
              required
              placeholder={t.lastNamePh}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ep-phone" className="text-xs sm:text-sm">
              {t.phone}
            </Label>
            <Input
              id="ep-phone"
              type="tel"
              required
              placeholder={t.phonePh}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-email" className="text-xs sm:text-sm">
              {t.email}
            </Label>
            <Input
              id="ep-email"
              type="email"
              required
              placeholder={t.emailPh}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ep-dob" className="text-xs sm:text-sm">
              {t.dob}
            </Label>
            <Input
              id="ep-dob"
              type="date"
              required
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-last-visit" className="text-xs sm:text-sm">
              {t.lastVisit}
            </Label>
            <Select value={lastVisit} onValueChange={setLastVisit} required>
              <SelectTrigger id="ep-last-visit" className="h-9 sm:h-10 text-sm">
                <SelectValue placeholder={t.selectOpt} />
              </SelectTrigger>
              <SelectContent>
                {t.lastVisitOpts.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ep-ins-changed" className="text-xs sm:text-sm">
              {t.insChanged}
            </Label>
            <Select
              value={insuranceChanged}
              onValueChange={setInsuranceChanged}
              required
            >
              <SelectTrigger
                id="ep-ins-changed"
                className="h-9 sm:h-10 text-sm"
              >
                <SelectValue placeholder={t.selectOpt} />
              </SelectTrigger>
              <SelectContent>
                {t.insChangedOpts.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-carrier" className="text-xs sm:text-sm">
              {t.carrier}
            </Label>
            <Input
              id="ep-carrier"
              placeholder={t.carrierPh}
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs sm:text-sm">{t.interested}</Label>
          <div className="grid grid-cols-1 gap-1.5 xs:grid-cols-2 sm:grid-cols-2">
            {interestOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground hover:border-accent/40 transition-colors"
              >
                <Checkbox
                  checked={interests.includes(opt.value)}
                  onCheckedChange={() => toggleInterest(opt.value)}
                />
                <span className="leading-snug">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ep-contact" className="text-xs sm:text-sm">
            {t.contactMethod}
          </Label>
          <Select
            value={contactMethod}
            onValueChange={setContactMethod}
            required
          >
            <SelectTrigger id="ep-contact" className="h-9 sm:h-10 text-sm">
              <SelectValue placeholder={t.selectOpt} />
            </SelectTrigger>
            <SelectContent>
              {t.contactOpts.map((o) => (
                <SelectItem key={o.v} value={o.v}>
                  {o.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-background px-3 py-3 text-xs text-muted-foreground">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => setConsent(v === true)}
            required
          />
          <span className="leading-relaxed">{t.consent}</span>
        </label>

        <Button
          type="submit"
          size="lg"
          disabled={!consent}
          className="w-full bg-gradient-accent text-sm text-accent-foreground font-bold shadow-glow hover:opacity-90 sm:text-base"
        >
          {t.submit}
        </Button>

        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t.secureNote}
          </p>
        </div>
      </form>
    </div>
  );
};

export default ExistingPatientForm;
